import * as React from 'react';
import {useEffect, useState, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {sha256} from 'js-sha256';

import Loader from '../../shared/Loader';
import {setUserToken} from '../../utils/api';
import CustomSelect from '../../shared/CustomSelect';

import FoodBank from './FoodBank';
import FoodInFoodOut from './FoodInFoodOut';
import NonFoodInNonFoodOut from './NonFoodInNonFoodOut';
import HumanResources from './HumanResources';
import Infrastructure from './Infrastructure';
import Energy from './Energy';
import Partners from './Partners';
import {
    DEFAULT_YEAR_LABEL,
    DEFAULT_YEAR_VALUE,
    fetchCheckpoints,
    fetchUoas,
} from './utils/queries';

import './styles.scss';

const ORGANIZATION_ROMANIA = '92';

const Feba = () => {
    const [searchParams] = useSearchParams();

    const container = searchParams.get('container_id');
    const containerId = searchParams.get('container_id');
    const typeId = searchParams.get('type_id');
    const uoaId = searchParams.get('uoa_id');

    const foodBank = searchParams.get('food_bank');
    const foodIn = searchParams.get('food_in');
    const nonFoodIn = searchParams.get('non_food_in');
    const human = searchParams.get('human');
    const infrastructure = searchParams.get('infrastructure');
    const energy = searchParams.get('energy');
    const partners = searchParams.get('partners');

    const hashKey = searchParams.get('key');
    const defaultCheckpoint = searchParams.get('default_checkpoint');

    const hashKeyLocal = sha256(process.env.REACT_APP_HASH_KEY_FEBA || '');

    setUserToken(searchParams.get('token') || '');

    const isAuthorized = Boolean(
        containerId && typeId && hashKey === hashKeyLocal,
    );

    const showCheckpointFilter = typeId !== ORGANIZATION_ROMANIA;

    const {data: uoas = [], isFetching: uoasLoading} = useQuery({
        queryKey: ['uoas', containerId, typeId],
        queryFn: () => fetchUoas(containerId!, typeId!),
        enabled: isAuthorized,
    });

    const {data: years = [], isFetching: yearsLoading} = useQuery({
        queryKey: ['checkpoints', containerId],
        queryFn: () => fetchCheckpoints(containerId!),
        enabled: isAuthorized,
    });

    const [selectedUoa, setSelectedUoa] = useState<string | null>(uoaId);
    const [selectedYear, setSelectedYear] = useState<string>();

    useEffect(() => setSelectedUoa(uoaId ?? 'All'), [uoas, uoaId]);

    useEffect(() => {
        const defaultYear = years.find(
            (item: {id: number; name: string}) =>
                Number(item.id) === Number(defaultCheckpoint),
        );

        setSelectedYear(defaultYear?.id ?? DEFAULT_YEAR_VALUE);
    }, [years, defaultCheckpoint]);

    const showAllData = useMemo(() => selectedYear !== 'All', [selectedYear]);

    const showUoaFilter = useMemo(
        () => !uoaId && uoas.length > 1,
        [uoas, uoaId],
    );

    const loading = uoasLoading || yearsLoading || !isAuthorized;

    if (loading || !selectedUoa || !selectedYear) {
        return (
            <div className='loader-container'>
                <Loader size={60} />
            </div>
        );
    }

    return (
        <div className='container space-y-8'>
            <div className='filter'>
                {showCheckpointFilter && (
                    <CustomSelect
                        items={years}
                        value={selectedYear}
                        onChange={(value: any) => setSelectedYear(value)}
                        label='Year'
                        className='filter-select'
                        defaultVariant={DEFAULT_YEAR_VALUE}
                        defaultLabel={DEFAULT_YEAR_LABEL}
                    />
                )}

                {showUoaFilter && (
                    <CustomSelect
                        items={uoas}
                        value={selectedUoa}
                        onChange={(value: any) => setSelectedUoa(value)}
                        label=''
                        className='filter-select'
                        defaultVariant='All'
                    />
                )}
            </div>

            <div>
                {showAllData && foodBank && (
                    <FoodBank
                        container={container}
                        selectedYear={selectedYear}
                        selectedUoa={selectedUoa}
                        survey={foodBank}
                    />
                )}

                {foodIn && (
                    <FoodInFoodOut
                        container={container}
                        selectedYear={selectedYear}
                        selectedUoa={selectedUoa}
                        survey={foodIn}
                    />
                )}

                {nonFoodIn && (
                    <NonFoodInNonFoodOut
                        container={container}
                        selectedYear={selectedYear}
                        selectedUoa={selectedUoa}
                        survey={nonFoodIn}
                    />
                )}

                {showAllData && human && (
                    <HumanResources
                        container={container}
                        selectedYear={selectedYear}
                        selectedUoa={selectedUoa}
                        survey={human}
                    />
                )}

                {showAllData && infrastructure && (
                    <Infrastructure
                        container={container}
                        selectedYear={selectedYear}
                        selectedUoa={selectedUoa}
                        survey={infrastructure}
                    />
                )}

                {showAllData && energy && (
                    <Energy
                        container={container}
                        selectedYear={selectedYear}
                        selectedUoa={selectedUoa}
                        survey={energy}
                    />
                )}

                {showAllData && partners && (
                    <Partners
                        container={container}
                        selectedYear={selectedYear}
                        selectedUoa={selectedUoa}
                        survey={partners}
                    />
                )}
            </div>
        </div>
    );
};

export default Feba;
